import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import isNil from 'lodash/isNil'
import './index.scss'
import { activeLayerIds, visibleLayerIds } from '../../../lib/layerVisibility'
import { isLayerBlocked, isLayerInMenu } from '../../../lib/layer-access'
import {
  displayedFrameAt, frameAt, frameTemplate, historyRanges, indexNearest, isRadarStep, isTimeLayer,
  driftWindBase, loopFrames, makeTimeline, makeTrack, nearestStop, nearestWindFieldUrl, offsetLabel,
  liveRevision, particleKindOf, particleTrackOf, precipFieldUrl, rainScaleOf, rangedTrack, runLabel, stampLabel, stepText,
  timeLayerKind, trackLayout, windFieldUrl
} from '../../../lib/timeLayers/timeline'
import { fetchIndexAt, fetchTimeLayerIndex } from '../../../lib/timeLayers/indexService'
import { clearFrameTemplates, setFrameTemplates, setLiveRevisions } from '../../../lib/timeLayers/frameStore'
import { prefetchFrames } from '../render-layers/map-types/timeLayer'
import { MIN_OPACITY, opacityFor, setLayerOpacity, subscribeOpacity } from '../../../lib/timeLayers/opacityStore'
import { googleWindMap } from '../../../lib/timeLayers/googleWindMap'
import { DEER_MOVEMENT_CAPTION, DEER_MOVEMENT_CAVEAT, DEER_MOVEMENT_NIGHT, DEER_MOVEMENT_RAMP, DEER_MOVEMENT_TICKS } from '../../../lib/timeLayers/deerMovement'
import { legendDescription, legendTicks, steppedGradient, weatherLegend } from '../../../lib/timeLayers/weatherLegends'
import { layerMenuTap } from '../../../action/layer-menu-actions'
import { userSettingsSwapLayers } from '../../../action/user-settings-actions'
import * as maptilersdk from '@maptiler/sdk'
// Verbatim copy of the module iOS and Android load into their 3D map; defines window.BMWind.
import '../../../lib/timeLayers/wind-particles'

// wind-particles.js builds its screen->geo lattice with map.transform.screenPointToLocation
// and window.maptilersdk.Point, falling back to map.unproject, which ray-casts the terrain
// and takes seconds per camera settle in 3D. The apps load the SDK as a global; web bundles
// it, so hand the module over. (The 2D Google adapter has no transform and keeps unproject.)
if (!window.maptilersdk) window.maptilersdk = maptilersdk

/**
 * The weather time bar, shown on the main map while a time-animated weather layer (Wind,
 * Temperature, Radar, ...) is on. Matt's "Weather time bar" design, matched with the iOS
 * and Android bars (TimeLayerBarView / TimeLayerController):
 *
 * - One layer animates at a time. The bar runs the lead layer's own frames; any other time
 *   layer that is on stays on its live tiles. The layer button opens a chip row that
 *   switches the weather layer without a trip to the layers menu (plan-allowed layers
 *   only), and layerMenuTap keeps one weather layer on at a time.
 * - Header: the layer, an Observed / Forecast / Live badge (click for the frame's model and
 *   run) and an X that turns the weather layer off. Then the frame time, its offset from
 *   now and, on archive layers, a history range (daily 7D/30D/90D/1Y, weekly 3M/6M/1Y).
 * - Track: one equal step per frame, cadence bands (observed white, forecast green,
 *   hatched gap), day labels and a NOW line; drag or click to scrub, arrows to step.
 * - Play loops the layer's whole track, radar included, thinned only above 128 frames. It
 *   starts once the first 8 frames' tiles are in and keeps loading the rest in playback
 *   order.
 *
 * Starts paused on "now", so the map looks exactly as it does without the bar until
 * someone touches it. Over Wind / Wind Gusts it also drives the wind streaks, and over
 * Radar and the precipitation layers the falling rain and snow (wind-particles.js); off
 * while playing, toggled by Flow and remembered per browser.
 */

const FLOW_KEY = 'bm.timeLayers.windFlow'
const REFRESH_MS = 60000
const FIELD_DEBOUNCE_MS = 300
const HOLD_MS = 1500
const FRAMES_BEFORE_PLAY = 8

const readFlow = () => {
  try { return localStorage.getItem(FLOW_KEY) !== 'off' } catch (e) { return true }
}
const writeFlow = on => {
  try { localStorage.setItem(FLOW_KEY, on ? 'on' : 'off') } catch (e) { /* private window */ }
}

// ---- Icons ----------------------------------------------------------------------------

const Svg = ({ size = 18, fill, children }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true"
    fill={fill ? 'currentColor' : 'none'} stroke={fill ? 'none' : 'currentColor'}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
)

// Icon paths from the "Weather Time Bar" design (TimeBar component).
const KIND_PATHS = {
  radar: 'M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0-6 0M12 12m-8 0a8 8 0 1 0 16 0a8 8 0 1 0-16 0M12 12l6-6',
  temperature: 'M14 14.76V5a2 2 0 0 0-4 0v9.76a4 4 0 1 0 4 0z',
  wind: 'M9.6 4.6A2 2 0 1 1 11 8H2M12.6 19.4A2 2 0 1 0 14 16H2M17.7 7.7A2.5 2.5 0 1 1 19.5 12H2',
  gusts: 'M3 8h10a2 2 0 1 0-2-2M3 12h14a2 2 0 1 1 2 2M3 16h8a2 2 0 1 1-2 2',
  cloud: 'M17.5 19a4.5 4.5 0 0 0 .5-9 7 7 0 0 0-13.5 2A4 4 0 0 0 6 19z',
  precip1h: 'M12 3l5 7a6 6 0 1 1-10 0z',
  precip24h: 'M8 3l3 5a3.5 3.5 0 1 1-6 0zM16 11l3 5a3.5 3.5 0 1 1-6 0z',
  snowfall: 'M12 2v20M2 12h20M5 5l14 14M19 5L5 19',
  chanceOfPrecip: 'M19 5L5 19M7.5 5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 1 0 0-5M16.5 14a2.5 2.5 0 1 0 0 5 2.5 2.5 0 1 0 0-5',
  snowDepth: 'M3 20h18M3 16c3-3 6 0 9-2s6-1 9-4M12 3v7M9 7l3 3 3-3',
  drought: 'M12 2v3M12 19v3M2 12h3M19 12h3M12 8a4 4 0 1 0 0 8 4 4 0 1 0 0-8M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2',
  deerMovement: 'M6.4 2.4c2.8 0 4 4.6 4 8.8 0 3.8-1.2 6.8-3.2 6.8s-3.8-2.4-3.6-6.2C3.8 6.8 4.6 2.4 6.4 2.4zM17.6 2.4c-2.8 0-4 4.6-4 8.8 0 3.8 1.2 6.8 3.2 6.8s3.6-2.4 3.6-6.2c-.2-5-1-9.4-2.8-9.4z',
  other: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 7v5l3 3'
}
const KindIcon = ({ kind, size = 16 }) => <Svg size={size}><path d={KIND_PATHS[kind] || KIND_PATHS.other} /></Svg>
const PlayIcon = () => <Svg size={18} fill><path d="M7 5l12 7-12 7z" /></Svg>
const PauseIcon = () => <Svg size={18} fill><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></Svg>
const StepIcon = ({ back }) => (
  <Svg size={18} fill><path d={back ? 'M6 5h2v14H6zM19 5l-10 7 10 7z' : 'M16 5h2v14h-2zM5 5l10 7-10 7z'} /></Svg>
)
const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
)
const CloseIcon = () => <Svg size={18}><path d="M6 6l12 12M18 6L6 18" /></Svg>

// ---- Track ----------------------------------------------------------------------------

const TRACK_HEIGHT = 44
// Everything is drawn this far down, so the NOW label above the line fits inside.
const TOP = 5
let patternSeq = 0

/**
 * The scrub track: cadence bands, frame and day ticks, the NOW line and the knob. Drag or
 * click anywhere on it to move the clock; arrows, Home and End step it from the keyboard.
 */
const TimeTrack = ({ times, forecast, nowIndex, step, utc, index, valueText, onScrubStart, onMove }) => {
  const ref = useRef(null)
  const [width, setWidth] = useState(0)
  const [patternId] = useState(() => `time-bar-gap-${++patternSeq}`)
  const dragging = useRef(false)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    setWidth(el.clientWidth)
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => setWidth(el.clientWidth))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const layout = useMemo(
    () => trackLayout({ times, forecast, nowIndex, step, width, utc }),
    [times, forecast, nowIndex, step, width, utc]
  )

  const indexAt = e => {
    const rect = ref.current.getBoundingClientRect()
    return indexNearest(layout, e.clientX - rect.left)
  }
  const onPointerDown = e => {
    if (e.button !== 0 || !layout.xs.length) return
    dragging.current = true
    e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId)
    onScrubStart()
    onMove(indexAt(e))
  }
  const onPointerMove = e => {
    if (dragging.current) onMove(indexAt(e))
  }
  const onPointerUp = () => { dragging.current = false }
  const onKeyDown = e => {
    const last = times.length - 1
    const to = { ArrowLeft: index - 1, ArrowDown: index - 1, ArrowRight: index + 1, ArrowUp: index + 1, Home: 0, End: last }[e.key]
    if (to === undefined) return
    e.preventDefault()
    onScrubStart()
    onMove(Math.max(0, Math.min(last, to)))
  }

  const knobX = layout.xs[index]
  return (
    <div
      ref={ref}
      className="time-layer-bar__track"
      role="slider"
      tabIndex={0}
      aria-label="Map time"
      aria-valuemin={0}
      aria-valuemax={Math.max(0, times.length - 1)}
      aria-valuenow={index}
      aria-valuetext={valueText}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
    >
      {width > 0 && (
        <svg width={width} height={TRACK_HEIGHT} aria-hidden="true">
          <defs>
            <pattern id={patternId} width="5" height="5" patternUnits="userSpaceOnUse">
              <rect width="2" height="5" fill="rgba(255,255,255,0.3)" />
            </pattern>
          </defs>
          {layout.segments.map((s, i) => (
            <g key={i}>
              <rect
                x={s.x} y={TOP + 8} width={Math.max(s.width - 2, 2)} height={4} rx={2}
                className={`time-layer-bar__band is-${s.kind}`}
                fill={s.kind === 'gap' ? `url(#${patternId})` : undefined}
              />
              {/* The cadence under each band wide enough to hold it. */}
              {s.kind !== 'gap' && s.width >= 30 && (
                <text x={s.x + s.width / 2} y={TOP + 29} className="time-layer-bar__cadence" textAnchor="middle" dominantBaseline="hanging">
                  {stepText(s.step).toUpperCase()}
                </text>
              )}
            </g>
          ))}
          {layout.showsFrameTicks && layout.xs.map((x, i) => (
            <rect key={i} x={x - 0.5} y={TOP + 13} width={1} height={3} className="time-layer-bar__tick" />
          ))}
          {layout.days.map((d, i) => (
            <g key={i}>
              <rect x={d.x - 0.5} y={TOP + 13} width={1} height={5} className="time-layer-bar__tick" />
              <text x={d.x} y={TOP + 19} className="time-layer-bar__day" textAnchor="middle" dominantBaseline="hanging">{d.text}</text>
            </g>
          ))}
          <rect x={layout.nowX - 1} y={TOP + 3} width={2} height={14} className="time-layer-bar__now-line" />
          <text
            x={Math.min(Math.max(12, layout.nowX), width - 12)} y={TOP - 5}
            className="time-layer-bar__now-label" textAnchor="middle" dominantBaseline="hanging"
          >
            NOW
          </text>
        </svg>
      )}
      {knobX != null && <span className="time-layer-bar__knob" style={{ left: knobX, top: TOP + 10 }} />}
    </div>
  )
}

// ---- Bar ------------------------------------------------------------------------------

/**
 * The bar's view of the time layers: every active one's track, the lead, the lead's
 * history range, its timeline and the clock. Rebuilt in one go so the clock always
 * belongs to the timeline it indexes.
 */
const buildCore = (prev, tracks, preferredLead, rangeChoice) => {
  const ids = Object.keys(tracks).map(Number)
  if (!ids.length) return null
  let leadId
  if (!isNil(preferredLead) && tracks[preferredLead]) leadId = preferredLead
  else if (prev && tracks[prev.leadId]) leadId = prev.leadId
  else leadId = ids.reduce((a, b) => (tracks[b].drawOrder > tracks[a].drawOrder ? b : a))
  const { track, range } = rangedTrack(tracks[leadId], rangeChoice[leadId])
  const timeline = makeTimeline([track])
  if (!timeline) return null
  // Keep the clock's place across an index refresh: same time if it still exists,
  // otherwise the nearest stop; "now" stays "now" as new frames arrive. A new lead or a
  // new range starts on "now".
  const same = prev && prev.leadId === leadId && prev.range === range
  const offNow = same && prev.index !== prev.timeline.nowIndex && prev.timeline.stops[prev.index] != null
  const index = offNow ? nearestStop(timeline, prev.timeline.stops[prev.index]) : timeline.nowIndex
  return { tracks, leadId, range, timeline, index }
}

// The deer layer's tiles come colored by movement against the hour's usual, so say what the colors mean.
export const DeerMovementLegend = () => (
  <div className="time-layer-bar__legend" role="img" title={DEER_MOVEMENT_CAVEAT}
    aria-label={`${DEER_MOVEMENT_CAPTION[0]} to ${DEER_MOVEMENT_CAPTION[1]}, -20 to +20 points; grey is night. ${DEER_MOVEMENT_CAVEAT}`}>
    <div className="time-layer-bar__legend-caption" aria-hidden="true">
      <span>{DEER_MOVEMENT_CAPTION[0]}</span>
      <span>for this time of day · lines every 5 pts</span>
      <span>{DEER_MOVEMENT_CAPTION[1]}</span>
    </div>
    <div className="time-layer-bar__legend-row" aria-hidden="true">
      <div className="time-layer-bar__legend-scale">
        <div className="time-layer-bar__legend-ramp" style={{ background: DEER_MOVEMENT_RAMP }} />
        <div className="time-layer-bar__legend-labels">
          {DEER_MOVEMENT_TICKS.map(t => (
            <span key={t.label} className="is-tick" style={{ left: `${t.at * 100}%` }}>{t.label}</span>
          ))}
        </div>
      </div>
      <div className="time-layer-bar__legend-night">
        <span className="time-layer-bar__legend-ramp" style={{ background: DEER_MOVEMENT_NIGHT }} />
        <div className="time-layer-bar__legend-labels"><span>Night</span></div>
      </div>
    </div>
  </div>
)

// The weather layers' tiles come coloured by class, so show the classes: one stepped bar per
// row (radar has rain and snow), labelled where the colour changes.
export const WeatherLegend = ({ kind }) => {
  const legend = weatherLegend(kind)
  if (!legend) return null
  return (
    <div className="time-layer-bar__legend is-weather" role="img" aria-label={legendDescription(legend)}>
      <div className="time-layer-bar__legend-caption" aria-hidden="true">
        <span>{legend.caption}</span>
        {legend.unit && <span>{legend.unit}</span>}
      </div>
      {legend.rows.map((row, i) => (
        <div key={i} className="time-layer-bar__legend-row" aria-hidden="true">
          {row.label && <span className="time-layer-bar__legend-row-label">{row.label}</span>}
          <div className="time-layer-bar__legend-scale">
            <div className="time-layer-bar__legend-ramp is-stepped" style={{ backgroundImage: steppedGradient(row) }}
              title={row.classes[0].name != null ? row.classes.map(c => `${c.name} ${c.title}`).join(' · ') : undefined} />
            <div className="time-layer-bar__legend-labels">
              {legendTicks(row).map(t => (
                <span key={t.label} style={{ left: `${t.at * 100}%` }}
                  className={t.at <= 0 ? 'is-start' : t.at >= 1 ? 'is-end' : 'is-mid'}>{t.label}</span>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// How see-through the shown layer draws, so the map under it reads; remembered per layer.
export const OpacityControl = ({ layerId }) => {
  const [value, setValue] = useState(() => opacityFor(layerId))
  useEffect(() => {
    setValue(opacityFor(layerId))
    return subscribeOpacity((id, v) => { if (id === layerId) setValue(v) })
  }, [layerId])
  const percent = Math.round(value * 100)
  return (
    <label className="time-layer-bar__opacity" title="Layer opacity">
      <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
        <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 1.5a6.5 6.5 0 0 1 0 13z" fill="currentColor" />
      </svg>
      <input type="range" min={MIN_OPACITY * 100} max={100} step={5} value={percent}
        aria-label="Layer opacity" aria-valuetext={`${percent}%`}
        onChange={e => setLayerOpacity(layerId, Number(e.target.value) / 100)} />
      <span className="time-layer-bar__opacity-value">{percent}%</span>
    </label>
  )
}

const TimeLayerBar = ({ timeLayers, catalog, mapReference, map3d, use3d, allowed, turnOn, swapLayers }) => {
  const [core, setCore] = useState(null)
  const [playing, setPlaying] = useState(false)
  const [loading, setLoading] = useState(null)       // 0..1 while play loads its first frames
  const [flowOn, setFlowOn] = useState(readFlow)
  const [message, setMessage] = useState(null)
  const [expanded, setExpanded] = useState(false)

  const coreRef = useRef(core)
  coreRef.current = core
  const preferredLead = useRef(null)
  const rangeChoice = useRef({})
  const playRef = useRef({ timer: null, prefetch: null, rest: null, cursor: 0, held: 0 })

  const timeline = core && core.timeline
  const index = core ? core.index : 0
  const leadId = core && core.leadId
  const layersKey = timeLayers.map(l => `${l.id}:${l.cloudfrontUrl}`).join('|')
  const active = allowed && timeLayers.length > 0

  const setIndex = next => setCore(c => {
    if (!c) return c
    const i = typeof next === 'function' ? next(c.index) : next
    const clamped = Math.max(0, Math.min(c.timeline.stops.length - 1, i))
    return clamped === c.index ? c : { ...c, index: clamped }
  })

  // ---- Indexes ------------------------------------------------------------------------

  useEffect(() => {
    if (!active) {
      setCore(null)
      setExpanded(false)
      return
    }
    let cancelled = false
    const load = () => Promise.all(timeLayers.map(layer =>
      fetchTimeLayerIndex(layer).then(index => (index ? makeTrack(layer, index) : null))
    )).then(loaded => {
      if (cancelled) return
      const tracks = {}
      loaded.filter(Boolean).forEach(t => { tracks[t.layerId] = t })
      // The bar keeps its last state until the new layer's index loads, so switching
      // layers from the chip row never blinks it out.
      setCore(prev => buildCore(prev, tracks, preferredLead.current, rangeChoice.current))
      if (tracks[preferredLead.current]) preferredLead.current = null
    })
    load()
    // New frames land every few minutes (radar every 10). The index service only goes to
    // the network once an index is older than its step, so ticking every minute is cheap.
    const timer = setInterval(load, REFRESH_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [active, layersKey])

  // ---- Frames -------------------------------------------------------------------------

  // Every time layer's live tiles carry their default frame's cache key, so a re-render of
  // that frame reaches browsers that cached the earlier one.
  const tracks = core && core.tracks
  useEffect(() => {
    setLiveRevisions(new Map(Object.values(tracks || {}).map(t => [t.layerId, liveRevision(t)])))
  }, [tracks])

  useEffect(() => {
    if (!timeline) {
      clearFrameTemplates()
      return
    }
    // Only the lead moves; every other time layer stays on its live tiles.
    const track = timeline.tracks[0]
    const frame = frameAt(timeline, track, index)
    setFrameTemplates(new Map([[track.layerId, frame ? frameTemplate(track, frame) : null]]))
    // Paused: warm the neighbouring frames so a step or a short scrub lands at once.
    if (!playing) {
      const near = [index - 1, index + 1]
        .map(i => frameAt(timeline, track, i))
        .filter(Boolean)
        .map(f => ({ layerId: track.layerId, template: frameTemplate(track, f) }))
      const warm = setTimeout(() => prefetchFrames(near), 400)
      return () => clearTimeout(warm)
    }
  }, [timeline, index, playing])

  // Leaving the map, hiding layers or turning the last time layer off: back to live tiles.
  useEffect(() => () => clearFrameTemplates(), [])

  // ---- Play ---------------------------------------------------------------------------

  const stopPlayback = () => {
    const p = playRef.current
    if (p.timer) clearInterval(p.timer)
    if (p.prefetch) p.prefetch.cancel()
    if (p.rest) p.rest()
    p.timer = null
    p.prefetch = null
    p.rest = null
    setLoading(null)
    setPlaying(false)
  }

  // The loop's remaining frames, a few at a time in playback order, while it runs.
  const warmRest = frames => {
    let stopped = false
    let cancel = null
    const next = at => {
      if (stopped || at >= frames.length) return
      const batch = prefetchFrames(frames.slice(at, at + FRAMES_BEFORE_PLAY))
      cancel = batch.cancel
      batch.promise.then(() => next(at + FRAMES_BEFORE_PLAY))
    }
    next(0)
    return () => {
      stopped = true
      cancel && cancel()
    }
  }

  const play = () => {
    const c = coreRef.current
    const tl = c && c.timeline
    if (!tl || tl.loop.length < 2) return
    const p = playRef.current
    // Start from the clock's place in the loop, or from the top if it is at the end.
    let cursor = tl.loop.findIndex(i => i >= c.index)
    if (cursor === -1 || cursor === tl.loop.length - 1) cursor = 0
    p.cursor = cursor
    p.held = 0
    setIndex(tl.loop[cursor])
    setPlaying(true)
    setLoading(0)
    const frames = loopFrames(tl, cursor).map(({ track, frame }) => ({ layerId: track.layerId, template: frameTemplate(track, frame) }))
    p.prefetch = prefetchFrames(frames.slice(0, FRAMES_BEFORE_PLAY), progress => setLoading(progress))
    p.prefetch.promise.then(({ loaded, failed }) => {
      if (!p.prefetch) return   // paused meanwhile
      p.prefetch = null
      setLoading(null)
      if (loaded + failed > 0 && failed / (loaded + failed) > 0.5) {
        stopPlayback()
        setMessage('Could not load the frames. Try again in a moment.')
        return
      }
      p.rest = warmRest(frames.slice(FRAMES_BEFORE_PLAY))
      // Radar's 10-minute frames run faster than hourly and daily ones.
      const interval = tl.finestStep <= 600 ? 300 : 500
      p.timer = setInterval(() => {
        const t = coreRef.current && coreRef.current.timeline
        if (!t || t.loop.length === 0) return
        // Hold on the last frame before wrapping, so the loop reads as a loop.
        if (p.cursor >= t.loop.length - 1) {
          p.held += interval
          if (p.held < HOLD_MS) return
          p.held = 0
        }
        p.cursor = (p.cursor + 1) % t.loop.length
        setIndex(t.loop[p.cursor])
      }, interval)
    })
  }

  const pause = () => {
    const p = playRef.current
    if (p.timer || p.prefetch) stopPlayback()
  }

  // A new lead, range or refreshed loop: playback restarts from the user's next Play.
  useEffect(() => {
    pause()
  }, [timeline && timeline.loop.length, leadId, core && core.range])

  useEffect(() => {
    if (!timeline) stopPlayback()
  }, [timeline])

  useEffect(() => {
    const onVisibility = () => { if (document.visibilityState === 'hidden') pause() }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      stopPlayback()
    }
  }, [])

  useEffect(() => {
    if (!message) return
    const t = setTimeout(() => setMessage(null), 4000)
    return () => clearTimeout(t)
  }, [message])

  // ---- Particles (wind streaks, rain and snow) ---------------------------------------

  const particleTrack = particleTrackOf(timeline)
  const kind = particleKindOf(particleTrack)
  const particleFrameRef = useRef(null)
  if (timeline && particleTrack && !playing) {
    // While playing nothing is fetched per frame; the frame play stops on loads on pause.
    particleFrameRef.current = displayedFrameAt(timeline, particleTrack, index)
  } else if (!particleTrack) {
    particleFrameRef.current = null
  }
  const particleFrame = particleFrameRef.current
  const fieldUrl = !particleTrack || !particleFrame ? null
    : kind === 'precip' ? precipFieldUrl(particleTrack, particleFrame) : windFieldUrl(particleTrack, particleFrame)

  // Rain and snow drift with the nearest hourly usa_wind field, read even when the Wind
  // layer is off. Without one they fall straight down.
  const [driftUrl, setDriftUrl] = useState(null)
  const driftBase = kind === 'precip' && particleTrack ? driftWindBase(particleTrack) : null
  const driftTime = kind === 'precip' && particleFrame ? particleFrame.time : null
  useEffect(() => {
    if (!driftBase || driftTime == null) {
      setDriftUrl(null)
      return
    }
    let cancelled = false
    fetchIndexAt(driftBase).then(index => {
      if (!cancelled) setDriftUrl(nearestWindFieldUrl(index, driftBase, driftTime))
    })
    return () => { cancelled = true }
  }, [driftBase, driftTime])

  // The layer's own rain scale, so a 1" hour and a 0.1" hour look different (Matt's retune).
  const rainScale = kind === 'precip' ? rainScaleOf(particleTrack) : null
  const rainScaleKey = rainScale ? `${rainScale.hours}/${rainScale.lo}/${rainScale.hi}` : ''

  const particleTarget = use3d ? map3d : mapReference

  useEffect(() => {
    const BMWind = window.BMWind
    if (!BMWind || !particleTarget || !particleTrack) return
    BMWind.install(use3d ? particleTarget : googleWindMap(particleTarget))
  }, [particleTarget, use3d, !!particleTrack])

  useEffect(() => {
    const BMWind = window.BMWind
    if (!BMWind) return
    if (!particleTrack || !particleTarget) {
      BMWind.setField({ url: null })
      BMWind.setEnabled(false)
      return
    }
    BMWind.setEnabled(flowOn && !playing)
    // Swapped after the scrubber rests, not for every frame it passes.
    const t = setTimeout(() => BMWind.setField(
      kind === 'precip' ? { url: fieldUrl, kind, windUrl: driftUrl, ...rainScale } : { url: fieldUrl, kind }
    ), FIELD_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [fieldUrl, kind, driftUrl, rainScaleKey, flowOn, playing, !!particleTrack, particleTarget])

  useEffect(() => () => { window.BMWind && window.BMWind.clear() }, [])

  // ---- Actions ------------------------------------------------------------------------

  const toggleFlow = () => {
    setFlowOn(on => {
      writeFlow(!on)
      return !on
    })
  }

  const step = delta => {
    pause()
    setIndex(i => i + delta)
  }

  /**
   * A chip in the layer row. An active layer just becomes the lead; any other is turned
   * on, and the one-weather-layer rule (layerMenuTap) turns the old one off in the same
   * save. The bar keeps its last state until the new index loads.
   */
  const pickLayer = layerId => {
    setExpanded(false)
    if (layerId === leadId) return
    pause()
    const c = coreRef.current
    if (c && c.tracks[layerId]) {
      preferredLead.current = null
      setCore(prev => buildCore(prev, prev.tracks, layerId, rangeChoice.current))
      return
    }
    const layer = catalog.find(l => l.id === layerId)
    if (!layer) return
    preferredLead.current = layerId
    turnOn(layer.layer)
  }

  const pickRange = label => {
    if (!core || core.range === label) return
    pause()
    rangeChoice.current = { ...rangeChoice.current, [leadId]: label }
    setCore(prev => prev && buildCore(prev, prev.tracks, prev.leadId, rangeChoice.current))
  }

  /** The X ends weather mode: every active time layer goes off, so the bar goes too. */
  const close = () => {
    pause()
    setExpanded(false)
    const ids = timeLayers.map(l => l.id)
    if (ids.length) swapLayers(ids)
  }

  // ---- View ---------------------------------------------------------------------------

  // What the track draws, which only changes with the timeline. One layer, so every stop
  // is one of its frames.
  const shape = useMemo(() => {
    if (!timeline) return null
    const track = timeline.tracks[0]
    const kinds = new Map(track.frames.map(f => [f.time, f.isForecast]))
    const forecast = timeline.stops.map(t => !!kinds.get(t))
    const hasObserved = forecast.includes(false)
    const nowIndex = hasObserved ? timeline.nowIndex : null
    const { steps } = trackLayout({ times: timeline.stops, forecast, nowIndex, step: track.step, width: 1, utc: true })
    return { track, forecast, hasObserved, nowIndex, steps }
  }, [timeline])

  const view = useMemo(() => {
    if (!shape) return null
    const { track, hasObserved } = shape
    const frame = displayedFrameAt(timeline, track, index)
    const isNow = hasObserved && index === timeline.nowIndex
    const badge = frame.isForecast ? 'forecast' : isNow && isRadarStep(track.step) ? 'live' : 'observed'
    // Relative to the layer's "now" frame; a forecast-only layer has none, so the wall clock.
    const reference = hasObserved ? timeline.stops[timeline.nowIndex] : Date.now()
    const source = []
    if (frame.model) source.push(frame.model)
    if (frame.isForecast && frame.runTime != null) source.push(`${runLabel(frame.runTime)} run`)
    const full = core.tracks[track.layerId] || track
    const { kind: layerKind } = timeLayerKind(track)
    const stamp = stampLabel(frame.time, track.step)
    const relative = isNow ? 'Now' : offsetLabel(frame.time, reference)
    const badgeText = { live: 'Live', forecast: 'Forecast', observed: 'Observed' }[badge]
    return {
      track,
      layerKind,
      stamp,
      relative,
      relativeTone: isNow ? 'now' : frame.isForecast ? 'forecast' : 'observed',
      badge,
      badgeText,
      badgeTitle: frame.isForecast && frame.runTime != null ? `Forecast · updated ${runLabel(frame.runTime)}` : badgeText,
      source: source.join(' · '),
      ranges: (historyRanges(full) || { options: [] }).options.map(o => o.label),
      valueText: `${stamp}, ${relative}, ${badgeText}`,
      stepText: stepText(shape.steps[index] || track.step)
    }
  }, [shape, index, core])

  const chips = useMemo(() => {
    if (!core) return []
    const layers = catalog.map(({ id, name, kind }) => ({ id, name, kind }))
    Object.values(core.tracks).forEach(t => {
      if (!layers.some(l => l.id === t.layerId)) layers.push({ id: t.layerId, name: t.name, kind: timeLayerKind(t) })
    })
    return layers
      .sort((a, b) => (a.kind.order !== b.kind.order ? a.kind.order - b.kind.order : a.name.localeCompare(b.name)))
      .map(l => ({ id: l.id, title: l.kind.short || l.name, kind: l.kind.kind, selected: l.id === leadId }))
  }, [catalog, core && core.tracks, leadId])

  const chipRowRef = useRef(null)
  useEffect(() => {
    // Bring the selected chip into view.
    const row = chipRowRef.current
    const selected = row && row.querySelector('.is-selected')
    if (selected) row.scrollLeft = Math.max(0, selected.offsetLeft - 16)
  }, [expanded])

  if (!active || !timeline || !view) return null

  const last = timeline.stops.length - 1

  return (
    <div className="time-layer-bar" role="group" aria-label="Weather time">
      {expanded && (
        <div className="time-layer-bar__chips" ref={chipRowRef} role="listbox" aria-label="Weather layers">
          {chips.map(chip => (
            <button
              key={chip.id}
              type="button"
              role="option"
              aria-selected={chip.selected}
              className={`time-layer-bar__chip${chip.selected ? ' is-selected' : ''}`}
              onClick={() => pickLayer(chip.id)}
            >
              <KindIcon kind={chip.kind} size={16} />
              {chip.title}
            </button>
          ))}
        </div>
      )}
      <div className="time-layer-bar__header">
        <button
          type="button"
          className="time-layer-bar__layer"
          aria-label="Switch weather layer"
          aria-expanded={expanded}
          onClick={() => setExpanded(e => !e)}
        >
          <span className="time-layer-bar__layer-icon"><KindIcon kind={view.layerKind} size={18} /></span>
          <span className="time-layer-bar__layer-name">{view.track.name}</span>
          <span className="time-layer-bar__chevron"><ChevronIcon /></span>
        </button>
        <span className="time-layer-bar__spacer" />
        <button
          type="button"
          className={`time-layer-bar__badge is-${view.badge}`}
          title={view.source || view.badgeTitle}
          aria-label={view.badgeTitle}
          onClick={() => view.source && setMessage(view.source)}
        >
          {view.badge === 'live' && <i className="time-layer-bar__badge-dot" />}
          {view.badgeText}
        </button>
        <button type="button" className="time-layer-bar__close" aria-label="Turn off weather layer" title="Turn off weather layer" onClick={close}>
          <CloseIcon />
        </button>
      </div>
      <div className="time-layer-bar__stamp-row">
        <span className="time-layer-bar__stamp">{view.stamp}</span>
        <span className={`time-layer-bar__relative is-${view.relativeTone}`}>{view.relative}</span>
        <span className="time-layer-bar__spacer" />
        {view.ranges.length > 0 && (
          <div className="time-layer-bar__ranges" role="radiogroup" aria-label="History range">
            {view.ranges.map(label => (
              <button
                key={label}
                type="button"
                role="radio"
                aria-checked={label === core.range}
                className={label === core.range ? 'is-selected' : ''}
                onClick={() => pickRange(label)}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
      {message && <div className="time-layer-bar__detail" role="status">{message}</div>}
      {view.layerKind === 'deerMovement' ? <DeerMovementLegend /> : <WeatherLegend kind={view.layerKind} />}
      <TimeTrack
        times={timeline.stops}
        forecast={shape.forecast}
        nowIndex={shape.nowIndex}
        step={view.track.step}
        utc={view.track.step >= 86400}
        index={index}
        valueText={view.valueText}
        onScrubStart={pause}
        onMove={setIndex}
      />
      <div className="time-layer-bar__controls">
        <button type="button" className="time-layer-bar__step" aria-label="Previous frame" disabled={index <= 0} onClick={() => step(-1)}>
          <StepIcon back />
        </button>
        <button type="button" className="time-layer-bar__play" aria-label={playing ? 'Pause' : 'Play'}
          disabled={timeline.loop.length < 2} onClick={() => (playing ? pause() : play())}>
          {!isNil(loading) && (
            <svg className="time-layer-bar__progress" viewBox="0 0 36 36" aria-hidden="true">
              <circle cx="18" cy="18" r="15" pathLength="100" strokeDasharray={`${Math.max(2, loading * 100)} 100`} />
            </svg>
          )}
          {playing ? <PauseIcon /> : <PlayIcon />}
        </button>
        <button type="button" className="time-layer-bar__step" aria-label="Next frame" disabled={index >= last} onClick={() => step(1)}>
          <StepIcon />
        </button>
        <span className="time-layer-bar__step-text">step {view.stepText}</span>
        <span className="time-layer-bar__spacer" />
        <OpacityControl layerId={view.track.layerId} />
        {particleTrack && (
          <button
            type="button"
            className={`time-layer-bar__flow${flowOn ? ' is-on' : ''}`}
            aria-pressed={flowOn}
            title={kind === 'precip'
              ? (flowOn ? 'Hide falling rain and snow' : 'Show falling rain and snow')
              : (flowOn ? 'Hide wind flow' : 'Show wind flow')}
            onClick={toggleFlow}
          >
            <KindIcon kind="wind" size={17} /> Flow
          </button>
        )}
      </div>
    </div>
  )
}

// The weather layers the chip row offers: every enabled, plan-allowed layer with a
// timeStep. Cached on its inputs, since mapStateToProps runs on every store change.
let catalogCache = { dict: null, subs: null, production: null, value: [] }
const weatherCatalog = (layersDictionary, userSubscriptions, productionLayers) => {
  const c = catalogCache
  if (c.dict === layersDictionary && c.subs === userSubscriptions && c.production === productionLayers) return c.value
  const value = Object.values(layersDictionary || {})
    .filter(l => isTimeLayer(l) && isLayerInMenu(l, userSubscriptions || [], productionLayers) && !isLayerBlocked(l, userSubscriptions || []))
    .map(l => ({ id: l.id, name: l.name, kind: timeLayerKind(l), layer: l }))
  catalogCache = { dict: layersDictionary, subs: userSubscriptions, production: productionLayers, value }
  return value
}

const mapStateToProps = state => {
  const ids = visibleLayerIds({
    activeIds: activeLayerIds(state.userSettings),
    layersDictionary: state.layersDictionary,
    layersVisible: state.layersVisible
  })
  const timeLayers = ids.map(id => state.layersDictionary?.[id]).filter(isTimeLayer)
  const mapReference = typeof state.mapReference === 'function' ? state.mapReference() : null
  const map3d = state.mapReferenceThreed && typeof state.mapReferenceThreed.getCanvas === 'function'
    ? state.mapReferenceThreed : null
  return {
    timeLayers,
    timeLayersKey: timeLayers.map(l => `${l.id}:${l.cloudfrontUrl}`).join('|'),
    catalog: weatherCatalog(state.layersDictionary, state.user && state.user.userSubscription, state.productionLayers),
    mapReference,
    map3d,
    use3d: !!state.is3dMapselected && !!map3d,
    allowed: state.selectedTab === 'map' && !!mapReference
  }
}

const mapDispatchToProps = dispatch => ({
  turnOn: layer => dispatch(layerMenuTap(layer, { active: false })),
  swapLayers: removeIds => dispatch(userSettingsSwapLayers(1, removeIds, null))
})

// timeLayers is a fresh array on every store change; compare it by its ids and URLs.
export default connect(mapStateToProps, mapDispatchToProps, null, {
  areStatePropsEqual: (next, prev) =>
    next.timeLayersKey === prev.timeLayersKey && next.catalog === prev.catalog && next.mapReference === prev.mapReference &&
    next.map3d === prev.map3d && next.use3d === prev.use3d && next.allowed === prev.allowed
})(TimeLayerBar)
